import { useEffect, useMemo, useState } from 'react';
import {
  FiUsers,
  FiEdit2,
  FiFilter,
  FiHash,
  FiPlus,
  FiSearch,
  FiTrash2,
  FiClock,
  FiCheckCircle,
  FiUpload,
  FiDownload,
  FiFolder,
  FiDollarSign,
  FiCreditCard,
  FiMapPin,
  FiFileText,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import ImportEmployeesModal from './ImportEmployeesModal';
import ImportEmployeeRecordModal from './ImportEmployeeRecordModal';
import EntityFilesModal from '../../components/EntityFilesModal';
import EmployeeBenefitsModal from '../../components/EmployeeBenefitsModal';
import EmployeeBankDataModal from '../../components/EmployeeBankDataModal';
import Api from '../../Api';
import {
  Button,
  Card,
  Input,
  Modal,
  PageComponent,
  Skeletons,
  Table,
  TableActions,
  EmployeeForm,
  Tooltip,
  Select,
  MultiSelect
} from '../../components';
import '../CompanyManager/style.css';
import { createEmployeeColumns } from './employeeColumns';
import { usePermission } from '../../hooks/usePermission';

export default function EmployeeManager() {
  const permissions = usePermission('management');
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [employeeToActivate, setEmployeeToActivate] = useState(null);
  const [isActivateModalOpen, setIsActivateModalOpen] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [company, setCompany] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [companyFilter, setCompanyFilter] = useState('all');
  const [companyFilterValues, setCompanyFilterValues] = useState(['all']);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isImportRecordModalOpen, setIsImportRecordModalOpen] = useState(false);
  const [filesModalOpen, setFilesModalOpen] = useState(false);
  const [filesCategory, setFilesCategory] = useState('geral');
  const [selectedEmployeeForFiles, setSelectedEmployeeForFiles] = useState(null);
  const [benefitsModalOpen, setBenefitsModalOpen] = useState(false);
  const [bankDataModalOpen, setBankDataModalOpen] = useState(false);
  const [selectedEmployeeForActions, setSelectedEmployeeForActions] = useState(null);
  const [stationsModalOpen, setStationsModalOpen] = useState(false);
  const [selectedEmployeeForStations, setSelectedEmployeeForStations] = useState(null);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [employeeToTransfer, setEmployeeToTransfer] = useState(null);
  const [transferTargetCompanyId, setTransferTargetCompanyId] = useState('');
  const [transferStationOptions, setTransferStationOptions] = useState([]);
  const [transferStationIds, setTransferStationIds] = useState([]);
  const [isLoadingTransferStations, setIsLoadingTransferStations] = useState(false);
  const [isTransferring, setIsTransferring] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [employeeForStatus, setEmployeeForStatus] = useState(null);
  const [newStatus, setNewStatus] = useState('Ativo');
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const navigate = useNavigate();
  const [isAdvancedFiltersOpen, setIsAdvancedFiltersOpen] = useState(false);
  const [advHasBenefit, setAdvHasBenefit] = useState('');
  const [advHasPosition, setAdvHasPosition] = useState('');
  const [advHasJourney, setAdvHasJourney] = useState('');
  const [advHasSalary, setAdvHasSalary] = useState('');
  const [advHasRegistration, setAdvHasRegistration] = useState('');
  const [advHasCpf, setAdvHasCpf] = useState('');
  const [advHasFiles, setAdvHasFiles] = useState('');
  const [stationOptions, setStationOptions] = useState([]);
  const [selectedStationIds, setSelectedStationIds] = useState(['all']);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const storedCompany = localStorage.getItem('selectedCompany');
        if (storedCompany) {
          setCompany(JSON.parse(storedCompany));
        }

        const companiesData = await Api.getCompanies();
        const companiesList = companiesData || [];
        setCompanies(companiesList);
        
        if (companiesList.length === 0) {
          setLoading(false);
        }
      } catch (e) {
        setError('Erro ao carregar dados iniciais.');
        setLoading(false);
      }
    };
    
    loadInitialData();
  }, []);

  useEffect(() => {
    if (companies.length > 0) {
      loadEmployees(companyFilter);
    }
  }, [companyFilter, companies]);

  useEffect(() => {
    const allValue = 'all';
    const ids = companyFilterValues.includes(allValue)
      ? companies.map(c => c.id)
      : companyFilterValues.filter(v => v !== allValue).map(v => Number(v));
    if (ids.length === 0 && companies.length === 0) {
      setStationOptions([]);
      return;
    }
    const targetIds = ids.length > 0 ? ids : companies.map(c => c.id);
    Promise.all(targetIds.map(id => Api.getServiceStations(id).catch(() => [])))
      .then(results => {
        const merged = results.flat().filter(Boolean);
        const map = new Map();
        merged.forEach(s => {
          if (!map.has(s.id)) {
            map.set(s.id, { value: String(s.id), label: s.nickname || s.name });
          }
        });
        setStationOptions([{ value: 'all', label: 'TODOS' }, ...Array.from(map.values())]);
      })
      .catch(() => setStationOptions([{ value: 'all', label: 'TODOS' }]));
  }, [companies, companyFilterValues]);

  useEffect(() => {
    if (!isTransferModalOpen) return;

    if (!transferTargetCompanyId) {
      setTransferStationOptions([]);
      return;
    }

    setIsLoadingTransferStations(true);
    Api.getServiceStations(transferTargetCompanyId)
      .then(stations => {
        const options = (stations || []).map(s => ({
          value: String(s.id),
          label: s.nickname || s.name,
        }));
        setTransferStationOptions(options);
      })
      .catch(() => setTransferStationOptions([]))
      .finally(() => setIsLoadingTransferStations(false));
  }, [isTransferModalOpen, transferTargetCompanyId]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, companyFilter]);

  function handleCompanyFilterChange(newValues) {
    const allValue = 'all';
    const currentSelected = companyFilterValues;
    const isAllSelected = newValues.includes(allValue);
    const wasAllSelected = currentSelected.includes(allValue);

    let nextValues;

    if (isAllSelected && !wasAllSelected) {
      const allIds = companies.map(c => String(c.id));
      nextValues = [allValue, ...allIds];
    } else if (!isAllSelected && wasAllSelected) {
      nextValues = [];
    } else if (wasAllSelected && newValues.length < currentSelected.length) {
      const remaining = newValues.filter(v => v !== allValue);
      nextValues = remaining;
    } else {
      const specificIds = newValues.filter(v => v !== allValue);
      const allIds = companies.map(c => String(c.id));
      if (specificIds.length === allIds.length && allIds.length > 0) {
        nextValues = [allValue, ...specificIds];
      } else {
        nextValues = specificIds;
      }
    }

    setCompanyFilterValues(nextValues);

    const idsWithoutAll = nextValues.filter(v => v !== allValue);
    if (idsWithoutAll.length === 1) {
      const id = idsWithoutAll[0];
      setCompanyFilter(id);
      loadEmployees(id);
    } else {
      setCompanyFilter('all');
      loadEmployees('all');
    }
  }

  function handleStationFilterChange(newValues) {
    const allValue = 'all';
    const currentSelected = selectedStationIds;
    const isAllSelected = newValues.includes(allValue);
    const wasAllSelected = currentSelected.includes(allValue);

    let nextValues;
    if (isAllSelected && !wasAllSelected) {
      const allIds = stationOptions.filter(o => o.value !== 'all').map(o => o.value);
      nextValues = [allValue, ...allIds];
    } else if (!isAllSelected && wasAllSelected) {
      nextValues = [];
    } else if (wasAllSelected && newValues.length < currentSelected.length) {
      nextValues = newValues.filter(v => v !== allValue);
    } else {
      const specificIds = newValues.filter(v => v !== allValue);
      const allIds = stationOptions.filter(o => o.value !== 'all').map(o => o.value);
      if (specificIds.length === allIds.length && allIds.length > 0) {
        nextValues = [allValue, ...specificIds];
      } else {
        nextValues = specificIds;
      }
    }
    setSelectedStationIds(nextValues);
  }

  async function loadEmployees(filterValue, companiesList = companies) {
    try {
      setLoading(true);
      let data = [];

      if (filterValue === 'all') {
        const listToUse = companiesList.length > 0 ? companiesList : companies;
        
        const promises = listToUse.map(c => 
          Api.getEmployees(c.id)
            .then(employees => employees.map(e => ({ ...e, company: c })))
            .catch(() => [])
        );
        const results = await Promise.all(promises);
        data = results.flat();
      } else {
        const employees = await Api.getEmployees(filterValue);
        const listToUse = companiesList.length > 0 ? companiesList : companies;
        const selectedComp = listToUse.find(c => c.id === Number(filterValue));
        data = employees.map(e => ({ ...e, company: selectedComp }));
      }

      if (Array.isArray(data)) {
        setEmployees(data);
      } else {
        setError('Não foi possível carregar os funcionários.');
      }
    } catch (err) {
      setError('Erro ao carregar funcionários. Tente novamente.');
    } finally {
      setLoading(false);
      setHasLoadedOnce(true);
    }
  }

  function handleCreate() {
    setSelectedEmployee(null);
    setIsModalOpen(true);
  }

  function handleEdit(employee) {
    setSelectedEmployee(employee);
    setIsModalOpen(true);
  }

  function handleFormSuccess() {
    loadEmployees(companyFilter);
    setIsModalOpen(false);
    setSelectedEmployee(null);
  }

  function handleRequestDelete(employee) {
    setEmployeeToDelete(employee);
    setIsDeleteModalOpen(true);
  }

  function handleRequestActivate(employee) {
    setEmployeeToActivate(employee);
    setIsActivateModalOpen(true);
  }

  async function handleConfirmDelete() {
    if (!employeeToDelete) return;
    
    const companyId = employeeToDelete.company?.id || employeeToDelete.company_id || (company && company.id);

    try {
      setIsDeleting(true);
      const result = await Api.deleteEmployee(companyId, employeeToDelete.id);

      if (result && !result.error) {
        setEmployees(prev => prev.map(e => e.id === employeeToDelete.id ? { ...e, is_active: false } : e));
        setIsDeleteModalOpen(false);
        setEmployeeToDelete(null);
      } else {
        alert(
          result && result.error
            ? result.error
            : 'Erro ao inativar funcionário.',
        );
      }
    } catch (err) {
      alert('Erro ao inativar funcionário.');
    } finally {
      setIsDeleting(false);
    }
  }

  async function handleConfirmActivate() {
    if (!employeeToActivate) return;
    
    const companyId = employeeToActivate.company?.id || employeeToActivate.company_id || (company && company.id);

    try {
      setIsActivating(true);
      const result = await Api.activateEmployee(companyId, employeeToActivate.id);

      if (result && !result.error) {
        setEmployees(prev => prev.map(e => e.id === employeeToActivate.id ? { ...e, is_active: true } : e));
        setIsActivateModalOpen(false);
        setEmployeeToActivate(null);
      } else {
        alert(
          result && result.error
            ? result.error
            : 'Erro ao ativar funcionário.',
        );
      }
    } catch (err) {
      alert('Erro ao ativar funcionário.');
    } finally {
      setIsActivating(false);
    }
  }

  function handleFiles(employee) {
    setSelectedEmployeeForFiles(employee);
    setFilesCategory('geral');
    setFilesModalOpen(true);
  }

  function handlePointFiles(employee) {
    setSelectedEmployeeForFiles(employee);
    setFilesCategory('ponto');
    setFilesModalOpen(true);
  }

  function handleBenefits(employee) {
    setSelectedEmployeeForActions(employee);
    setBenefitsModalOpen(true);
  }

  function handleBankData(employee) {
    setSelectedEmployeeForActions(employee);
    setBankDataModalOpen(true);
  }

  function handleBenefitsSuccess() {
    loadEmployees(companyFilter);
    setBenefitsModalOpen(false);
    setSelectedEmployeeForActions(null);
  }

  function handleBankDataSuccess() {
    loadEmployees(companyFilter);
    setBankDataModalOpen(false);
    setSelectedEmployeeForActions(null);
  }

  function handleViewStations(employee) {
    setSelectedEmployeeForStations(employee);
    setStationsModalOpen(true);
  }

  function handleOpenTransfer(employee) {
    setEmployeeToTransfer(employee);
    setTransferTargetCompanyId('');
    setTransferStationIds([]);
    setTransferStationOptions([]);
    setIsLoadingTransferStations(false);
    setIsTransferModalOpen(true);
  }

  function handleCloseTransfer() {
    if (isTransferring) return;
    setIsTransferModalOpen(false);
    setEmployeeToTransfer(null);
    setTransferTargetCompanyId('');
    setTransferStationIds([]);
    setTransferStationOptions([]);
    setIsLoadingTransferStations(false);
  }

  async function handleConfirmTransfer() {
    if (!employeeToTransfer) return;

    const sourceCompanyId =
      employeeToTransfer.company?.id ||
      employeeToTransfer.company_id ||
      (company && company.id);

    if (!sourceCompanyId) {
      alert('Não foi possível identificar a empresa atual do funcionário.');
      return;
    }

    if (!transferTargetCompanyId) {
      alert('Selecione a empresa de destino.');
      return;
    }

    const targetCompanyId = Number(transferTargetCompanyId);
    if (!targetCompanyId) {
      alert('Selecione uma empresa de destino válida.');
      return;
    }

    const stationIds = (transferStationIds || [])
      .map(v => Number(v))
      .filter(v => Number.isFinite(v) && v > 0);

    try {
      setIsTransferring(true);
      const result = await Api.transferEmployee(sourceCompanyId, employeeToTransfer.id, {
        target_company_id: targetCompanyId,
        service_station_ids: stationIds,
      });

      if (result && !result.error) {
        handleCloseTransfer();
        loadEmployees(companyFilter);
      } else {
        alert(
          result && result.error
            ? result.error
            : 'Erro ao transferir funcionário.',
        );
      }
    } catch (err) {
      alert('Erro ao transferir funcionário.');
    } finally {
      setIsTransferring(false);
    }
  }

  function handleCancelDelete() {
    if (isDeleting) return;

    setIsDeleteModalOpen(false);
    setEmployeeToDelete(null);
  }

  function handleCancelActivate() {
    if (isActivating) return;

    setIsActivateModalOpen(false);
    setEmployeeToActivate(null);
  }

  function handleOpenStatusModal(employee) {
    const initialStatus = employee.status || (employee.is_active ? 'Ativo' : 'Inativo');
    setEmployeeForStatus(employee);
    setNewStatus(initialStatus);
    setIsStatusModalOpen(true);
  }

  async function handleConfirmStatusUpdate() {
    if (!employeeForStatus) return;

    const companyId =
      employeeForStatus.company?.id ||
      employeeForStatus.company_id ||
      (company && company.id);

    if (!companyId) {
      alert('Não foi possível identificar a empresa do funcionário.');
      return;
    }

    const nextIsActive = newStatus === 'Inativo' ? false : true;

    try {
      setIsUpdatingStatus(true);
      const result = await Api.updateEmployee(companyId, employeeForStatus.id, {
        status: newStatus,
        is_active: nextIsActive,
      });

      if (result && !result.error) {
        setEmployees(prev =>
          prev.map(e =>
            e.id === employeeForStatus.id
              ? { ...e, status: newStatus, is_active: nextIsActive }
              : e,
          ),
        );
        setIsStatusModalOpen(false);
        setEmployeeForStatus(null);
      } else {
        alert(
          result && result.error
            ? result.error
            : 'Erro ao atualizar status do funcionário.',
        );
      }
    } catch (err) {
      alert('Erro ao atualizar status do funcionário.');
    } finally {
      setIsUpdatingStatus(false);
    }
  }

  function handleCancelStatusUpdate() {
    if (isUpdatingStatus) return;
    setIsStatusModalOpen(false);
    setEmployeeForStatus(null);
  }

  function handleExport() {
    if (!employees || employees.length === 0) {
      alert('Não há funcionários para exportar.');
      return;
    }

    const exportData = employees.map(emp => ({
      'Empresa': emp.company ? (emp.company.corporate_name || emp.company.trade_name || `Empresa ${emp.company.id}`) : 'N/A',
      Nome: emp.name,
      Email: emp.email || '',
      CPF: emp.cpf || '',
      'Matrícula': emp.registration_number || '',
      'Tipo': emp.type === 'F' ? 'Freelancer' : 'Colaborador',
      'Cargo': emp.position?.name || '',
      'Postos Vinculados': emp.serviceStations?.map(s => s.nickname || s.name).join(', ') || '',
      'Status': emp.is_active ? 'Ativo' : 'Inativo',
      'Salário': emp.salary || ''
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Funcionários");
    XLSX.writeFile(wb, "funcionarios.xlsx");
  }

  const companyOptions = useMemo(() => [
    { label: 'TODAS', value: 'all' },
    ...companies.map(c => ({
      label: c.corporate_name || c.trade_name || `Empresa ${c.id}`,
      value: String(c.id)
    }))
  ], [companies]);

  const filteredEmployees = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const allValue = 'all';
    const specificIds = companyFilterValues.filter(v => v !== allValue);
    const hasAllSelected = companyFilterValues.includes(allValue);
    const selectedSpecificStations = selectedStationIds.filter(v => v !== allValue);
    const hasAllStations = selectedStationIds.includes(allValue);

    return employees.filter(employee => {
      const matchesSearch =
        !term ||
        (employee.name || '').toLowerCase().includes(term) ||
        (employee.email || '').toLowerCase().includes(term) ||
        (employee.cpf || '').toLowerCase().includes(term) ||
        (employee.registration_number || '').toLowerCase().includes(term);

      let matchesStatus = true;

      if (statusFilter !== 'all') {
        const empStatus = employee.status || (employee.is_active ? 'Ativo' : 'Inativo');
        matchesStatus = empStatus === statusFilter;
      }

      const companyId = employee.company?.id || employee.company_id;
      const matchesCompany =
        hasAllSelected ||
        specificIds.length === 0 ||
        specificIds.includes(String(companyId));

      let matchesStation = true;
      if (!hasAllStations && selectedSpecificStations.length > 0) {
        const stations = Array.isArray(employee.serviceStations) ? employee.serviceStations : [];
        const stationIds = stations.map(s => String(s.id));
        matchesStation = stationIds.some(id => selectedSpecificStations.includes(String(id)));
      }

      let matchesAdvanced = true;
      if (advHasBenefit) {
        const hasBenefit = Array.isArray(employee.benefits) && employee.benefits.length > 0;
        if (advHasBenefit === 'yes') matchesAdvanced = matchesAdvanced && hasBenefit;
        if (advHasBenefit === 'no') matchesAdvanced = matchesAdvanced && !hasBenefit;
      }
      if (advHasPosition) {
        const hasPosition = !!(employee.position && employee.position.name);
        if (advHasPosition === 'yes') matchesAdvanced = matchesAdvanced && hasPosition;
        if (advHasPosition === 'no') matchesAdvanced = matchesAdvanced && !hasPosition;
      }
      if (advHasJourney) {
        const hasJourney = !!(employee.journey && employee.journey.name);
        if (advHasJourney === 'yes') matchesAdvanced = matchesAdvanced && hasJourney;
        if (advHasJourney === 'no') matchesAdvanced = matchesAdvanced && !hasJourney;
      }
      if (advHasSalary) {
        const hasSalary = employee.salary != null && String(employee.salary).trim() !== '';
        if (advHasSalary === 'yes') matchesAdvanced = matchesAdvanced && hasSalary;
        if (advHasSalary === 'no') matchesAdvanced = matchesAdvanced && !hasSalary;
      }
      if (advHasRegistration) {
        const hasReg = !!(employee.registration_number && String(employee.registration_number).trim() !== '');
        if (advHasRegistration === 'yes') matchesAdvanced = matchesAdvanced && hasReg;
        if (advHasRegistration === 'no') matchesAdvanced = matchesAdvanced && !hasReg;
      }
      if (advHasCpf) {
        const hasCpf = !!(employee.cpf && String(employee.cpf).trim() !== '');
        if (advHasCpf === 'yes') matchesAdvanced = matchesAdvanced && hasCpf;
        if (advHasCpf === 'no') matchesAdvanced = matchesAdvanced && !hasCpf;
      }
      if (advHasFiles) {
        const hasFiles = (Number(employee.filesCount) || 0) > 0;
        if (advHasFiles === 'yes') matchesAdvanced = matchesAdvanced && hasFiles;
        if (advHasFiles === 'no') matchesAdvanced = matchesAdvanced && !hasFiles;
      }

      return matchesSearch && matchesStatus && matchesCompany && matchesStation && matchesAdvanced;
    });
  }, [employees, searchTerm, statusFilter, companyFilterValues, selectedStationIds, advHasBenefit, advHasPosition, advHasJourney, advHasSalary, advHasRegistration, advHasCpf, advHasFiles]);

  const totalItems = filteredEmployees.length;
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / pageSize) : 1;
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const paginatedEmployees = filteredEmployees.slice(
    startIndex,
    startIndex + pageSize,
  );

  const columns = createEmployeeColumns({
    onEdit: permissions.canEdit ? handleEdit : null,
    onTransfer: permissions.canEdit ? handleOpenTransfer : null,
    onFiles: handleFiles,
    onPointFiles: handlePointFiles,
    onBenefits: permissions.canEdit ? handleBenefits : null,
    onBankData: permissions.canEdit ? handleBankData : null,
    onRequestDelete: permissions.canDelete ? handleRequestDelete : null,
    onRequestActivate: permissions.canDelete ? handleRequestActivate : null,
    onChangeStatus: permissions.canEdit ? handleOpenStatusModal : null,
    onViewStations: permissions.canEdit ? handleViewStations : null,
    showCompanyColumn: true,
    showStationsColumn: true,
  });

  function EmployeeManagerSkeletonWrapper() {
    const { CompanyManagerSkeleton } = Skeletons;
    return <CompanyManagerSkeleton />;
  }

  const isMobile = window.innerWidth <= 768;

  if (!permissions.canView) {
    return (
      <PageComponent authenticatedPage={true}>
        <div className="container">
          <div className="manager-header">
            <div className="manager-header-title">
              <div className="manager-header-icon">
                <FiUsers size={22} />
              </div>
              <div>
                <h1>Acesso Negado</h1>
                <p>Você não tem permissão para visualizar esta página.</p>
              </div>
            </div>
          </div>
        </div>
      </PageComponent>
    );
  }

  return (
    <PageComponent authenticatedPage={true}>
      <div className="container">
        {loading && !hasLoadedOnce && <EmployeeManagerSkeletonWrapper />}

        {(hasLoadedOnce || !loading) && (
          <>
            <div className="manager-header">
              <div className="manager-header-title">
                <div className="manager-header-icon">
                  <FiUsers size={22} />
                </div>
                <div>
                  <h1>Funcionários</h1>
                  <p>
                    Visualize, filtre e gerencie todos os funcionários da empresa
                    selecionada.
                  </p>
                </div>
              </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Button onClick={handleExport} className="manager-header-button" variant="secondary">
              <FiDownload size={18} />
              {!isMobile && 'Exportar'}
            </Button>
            {permissions.canCreate && (
              <>
                <Button onClick={() => setIsImportModalOpen(true)} className="manager-header-button" variant="secondary">
                  <FiUpload size={18} />
                  {!isMobile && 'Importar'}
                </Button>
                <Button onClick={() => setIsImportRecordModalOpen(true)} className="manager-header-button" variant="secondary">
                  <FiFileText size={18} />
                  {!isMobile && 'Importar Ficha'}
                </Button>
              </>
            )}
            {permissions.canCreate && (
              <Button onClick={handleCreate} className="manager-header-button">
                <FiPlus size={18} />
                Funcionário
              </Button>
            )}
          </div>
        </div>

            <Card className="manager-filters-card">
              <div className="manager-filters-header">
                <div className="manager-filters-title">
                  <FiFilter size={16} />
                  <span>Filtros</span>
                </div>
                {employees.length > 0 && (
                  <span className="manager-filters-count">
                    {filteredEmployees.length} de {employees.length} funcionários
                  </span>
                )}
                <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
                  <Button
                    onClick={() => setIsAdvancedFiltersOpen(true)}
                    className="manager-header-button"
                    variant="secondary"
                  >
                    <FiFilter size={16} />
                    {!isMobile && 'Filtros Avançados'}
                  </Button>
                  {(advHasBenefit || advHasPosition || advHasJourney || advHasSalary || advHasRegistration || advHasCpf || advHasFiles) && (
                    <Button
                      onClick={() => {
                        setAdvHasBenefit('');
                        setAdvHasPosition('');
                        setAdvHasJourney('');
                        setAdvHasSalary('');
                        setAdvHasRegistration('');
                        setAdvHasCpf('');
                        setAdvHasFiles('');
                      }}
                      className="manager-header-button"
                      variant="secondary"
                    >
                      Limpar Filtros
                    </Button>
                  )}
                </div>
              </div>

              <div className="manager-filters-grid-custom">
                <div className="manager-filter-group">
                  <MultiSelect
                    id="company-filter"
                    label="Empresa"
                    options={companyOptions}
                    value={companyFilterValues}
                    onChange={handleCompanyFilterChange}
                    searchable
                  />
                </div>

                <div className="manager-filter-group">
                  <MultiSelect
                    id="station-filter"
                    label="Posto de Serviço"
                    options={stationOptions}
                    value={selectedStationIds}
                    onChange={handleStationFilterChange}
                    searchable
                  />
                </div>

                <div className="manager-filter-group">
                  <label className="manager-filter-label">Buscar</label>
                  <div className="manager-filter-search">
                    <Input
                      id="employee-search"
                      placeholder="Busque por nome, e-mail, CPF ou matrícula"
                      value={searchTerm}
                      onChange={event =>
                        setSearchTerm(event.target.value)
                      }
                      icon={FiSearch}
                    />
                  </div>
                </div>

                <div className="manager-filter-group">
                  <Select
                     label="Status"
                     options={[
                       { value: 'all', label: 'Todos' },
                       { value: 'Ativo', label: 'Ativos', color: 'var(--success-color)' },
                       { value: 'Inativo', label: 'Inativos', color: 'var(--error-color)' },
                       { value: 'Atestado Médico', label: 'Atestado Médico', color: 'var(--info-color)' },
                       { value: 'Afastado INSS', label: 'Afastado INSS', color: 'var(--warning-color)' },
                       { value: 'Férias', label: 'Férias', color: 'var(--warning-color)' },
                     ]}
                     value={statusFilter}
                     onChange={e => setStatusFilter(e.target.value)}
                     useCustomDropdown={true}
                   />
                </div>
              </div>
            </Card>

            {error && <div className="error">{error}</div>}

            {!loading && !error && employees.length === 0 && (
              <div className="empty-state">
                <p>Nenhum funcionário cadastrado.</p>
              </div>
            )}

            {!loading && !error && employees.length > 0 && (
              <Table
                items={paginatedEmployees}
                columns={columns}
                totalItems={totalItems}
                page={safePage}
                totalPages={totalPages}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                onPageSizeChange={value => {
                  setPageSize(value);
                  setCurrentPage(1);
                }}
                getRowKey={employee => employee.id}
                emptyMessage="Nenhum funcionário encontrado com os filtros atuais."
                onRowDoubleClick={(employee) => handleEdit(employee)}
              />
            )}
          </>
        )}
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={selectedEmployee ? 'Editar Funcionário' : 'Novo Funcionário'}
        closeOnOverlayClick={false}
        size='xl'
      >
        <EmployeeForm
          companyId={companyFilter !== 'all' ? companyFilter : (company ? company.id : null)}
          companies={companies}
          initialData={selectedEmployee}
          onSuccess={handleFormSuccess}
          onCancel={() => setIsModalOpen(false)}
        />
      </Modal>

      <Modal
        isOpen={isAdvancedFiltersOpen}
        onClose={() => setIsAdvancedFiltersOpen(false)}
        title="Filtros Avançados"
        width="600px"
      >
        <div style={{ padding: '1rem' }}>
          <div className="form-grid">
            <Select
              id="advHasBenefit"
              label="Benefício vinculado"
              options={[
                { value: '', label: 'TODOS' },
                { value: 'yes', label: 'Sim' },
                { value: 'no', label: 'Não' },
              ]}
              value={advHasBenefit}
              onChange={(e) => setAdvHasBenefit(e.target.value)}
            />
            <Select
              id="advHasPosition"
              label="Cargo vinculado"
              options={[
                { value: '', label: 'TODOS' },
                { value: 'yes', label: 'Sim' },
                { value: 'no', label: 'Não' },
              ]}
              value={advHasPosition}
              onChange={(e) => setAdvHasPosition(e.target.value)}
            />
            <Select
              id="advHasJourney"
              label="Jornada vinculada"
              options={[
                { value: '', label: 'TODOS' },
                { value: 'yes', label: 'Sim' },
                { value: 'no', label: 'Não' },
              ]}
              value={advHasJourney}
              onChange={(e) => setAdvHasJourney(e.target.value)}
            />
            <Select
              id="advHasSalary"
              label="Salário preenchido"
              options={[
                { value: '', label: 'TODOS' },
                { value: 'yes', label: 'Sim' },
                { value: 'no', label: 'Não' },
              ]}
              value={advHasSalary}
              onChange={(e) => setAdvHasSalary(e.target.value)}
            />
            <Select
              id="advHasRegistration"
              label="Matrícula preenchida"
              options={[
                { value: '', label: 'TODOS' },
                { value: 'yes', label: 'Sim' },
                { value: 'no', label: 'Não' },
              ]}
              value={advHasRegistration}
              onChange={(e) => setAdvHasRegistration(e.target.value)}
            />
            <Select
              id="advHasCpf"
              label="CPF preenchido"
              options={[
                { value: '', label: 'TODOS' },
                { value: 'yes', label: 'Sim' },
                { value: 'no', label: 'Não' },
              ]}
              value={advHasCpf}
              onChange={(e) => setAdvHasCpf(e.target.value)}
            />
            <Select
              id="advHasFiles"
              label="Arquivos vinculados"
              options={[
                { value: '', label: 'TODOS' },
                { value: 'yes', label: 'Sim' },
                { value: 'no', label: 'Não' },
              ]}
              value={advHasFiles}
              onChange={(e) => setAdvHasFiles(e.target.value)}
            />
          </div>
        </div>
        <div className="modal-actions">
            <Button
              type="button"
              className="cancel-button"
              onClick={() => setIsAdvancedFiltersOpen(false)}
            >
              Fechar
            </Button>
            <Button
              type="button"
              onClick={() => setIsAdvancedFiltersOpen(false)}
            >
              Aplicar
            </Button>
          </div>
      </Modal>

      <ImportEmployeesModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onSuccess={() => {
          setIsImportModalOpen(false);
          loadEmployees(companyFilter);
        }}
        companyId={companyFilter !== 'all' ? companyFilter : null}
        companies={companies}
      />

      <ImportEmployeeRecordModal
        isOpen={isImportRecordModalOpen}
        onClose={() => setIsImportRecordModalOpen(false)}
        companyId={companyFilter !== 'all' ? companyFilter : (company ? company.id : null)}
        onSuccess={() => loadEmployees(companyFilter)}
      />

      <EntityFilesModal
        isOpen={filesModalOpen}
        onClose={() => {
          setFilesModalOpen(false);
          setSelectedEmployeeForFiles(null);
          setFilesCategory('geral');
        }}
        entity={selectedEmployeeForFiles}
        entityType="employee"
        company={selectedEmployeeForFiles?.company || company}
        category={filesCategory}
      />

      <EmployeeBenefitsModal
        isOpen={benefitsModalOpen}
        onClose={() => setBenefitsModalOpen(false)}
        companyId={company?.id}
        employee={selectedEmployeeForActions}
        onSuccess={handleBenefitsSuccess}
      />

      <EmployeeBankDataModal
        isOpen={bankDataModalOpen}
        onClose={() => setBankDataModalOpen(false)}
        companyId={company?.id}
        employee={selectedEmployeeForActions}
        onSuccess={handleBankDataSuccess}
      />

      <Modal
        isOpen={isStatusModalOpen}
        onClose={handleCancelStatusUpdate}
        title="Alterar status do funcionário"
        width="400px"
      >
        <div className="delete-modal-content">
          <p>
            Selecione o novo status para o funcionário{' '}
            <strong>{employeeForStatus?.name}</strong>.
          </p>
          <div style={{ marginTop: '1rem', marginBottom: '1rem' }}>
            <Select
              id="status"
              label="Novo status"
              value={newStatus}
              onChange={e => setNewStatus(e.target.value)}
              options={[
                { value: 'Ativo', label: 'Ativo', color: 'var(--success-color)' },
                { value: 'Inativo', label: 'Inativo', color: 'var(--error-color)' },
                { value: 'Atestado Médico', label: 'Atestado Médico', color: 'var(--info-color)' },
                { value: 'Afastado INSS', label: 'Afastado INSS', color: 'var(--warning-color)' },
                { value: 'Férias', label: 'Férias', color: 'var(--warning-color)' },
              ]}
              useCustomDropdown={true}
            />
          </div>
        </div>
        <div className="delete-modal-actions">
            <Button
              type="button"
              className="cancel-button"
              onClick={handleCancelStatusUpdate}
              disabled={isUpdatingStatus}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={handleConfirmStatusUpdate}
              disabled={isUpdatingStatus}
            >
              {isUpdatingStatus ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
      </Modal>

      <Modal
        isOpen={isTransferModalOpen}
        onClose={handleCloseTransfer}
        title={`Transferir funcionário${employeeToTransfer?.name ? ` - ${employeeToTransfer.name}` : ''}`}
        width="600px"
        closeOnOverlayClick={false}
      >
        <div style={{ padding: '1rem' }}>
          <div className="form-grid">
            <Select
              id="transferCompany"
              label="Empresa de destino"
              value={transferTargetCompanyId}
              onChange={e => {
                setTransferTargetCompanyId(e.target.value);
                setTransferStationIds([]);
              }}
              options={[
                { value: '', label: 'Selecione...' },
                ...companies
                  .filter(c => String(c.id) !== String(employeeToTransfer?.company?.id || employeeToTransfer?.company_id))
                  .map(c => ({
                    value: String(c.id),
                    label: c.corporate_name || c.trade_name || `Empresa ${c.id}`,
                  })),
              ]}
              useCustomDropdown={true}
            />

            <MultiSelect
              id="transferStations"
              label="Postos do funcionário (empresa destino)"
              options={transferStationOptions}
              value={transferStationIds}
              onChange={setTransferStationIds}
              searchable
              disabled={!transferTargetCompanyId || isLoadingTransferStations}
            />
          </div>

          {isLoadingTransferStations && (
            <p style={{ marginTop: '0.75rem', color: '#6b7280' }}>
              Carregando postos...
            </p>
          )}
        </div>

        <div className="delete-modal-actions">
          <Button
            type="button"
            className="cancel-button"
            onClick={handleCloseTransfer}
            disabled={isTransferring}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleConfirmTransfer}
            disabled={isTransferring}
          >
            {isTransferring ? 'Transferindo...' : 'Transferir'}
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={stationsModalOpen}
        onClose={() => setStationsModalOpen(false)}
        title={`Postos de Serviço - ${selectedEmployeeForStations?.name}`}
        width="600px"
      >
        <div style={{ padding: '1rem' }}>
             {selectedEmployeeForStations?.serviceStations?.length > 0 ? (
                 <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                     {selectedEmployeeForStations.serviceStations.map(station => (
                         <li key={station.id} style={{ 
                             padding: '0.75rem', 
                             borderBottom: '1px solid #eee',
                             display: 'flex',
                             alignItems: 'center',
                             gap: '0.75rem'
                         }}>
                             <div style={{ 
                                 width: '32px', 
                                 height: '32px', 
                                 borderRadius: '50%', 
                                 backgroundColor: '#f3f4f6', 
                                 display: 'flex', 
                                 alignItems: 'center', 
                                 justifyContent: 'center',
                                 color: '#6b7280'
                             }}>
                                 <FiMapPin size={16} />
                             </div>
                             <div>
                                 <strong style={{ display: 'block', color: '#374151' }}>{station.nickname || station.name}</strong>
                                 <span style={{ fontSize: '0.85rem', color: '#9ca3af' }}>{station.corporate_name || station.name}</span>
                             </div>
                         </li>
                     ))}
                 </ul>
             ) : (
                 <p style={{ color: '#6b7280', textAlign: 'center' }}>Nenhum posto vinculado.</p>
             )}
             <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
                 <Button onClick={() => setStationsModalOpen(false)}>Fechar</Button>
             </div>
        </div>
      </Modal>
    </PageComponent>
  );
}
